(ns ui.data-grid.preview
  (:require
   ["ag-grid-react" :refer [AgGridReact]]
   ["copy-to-clipboard" :as copy]
   ["papaparse" :as papaparse]
   [applied-science.js-interop :as j]
   [ui.data-grid.column-resize :as column-resize]
   [ui.data-grid.condition-value-filter :as condition-value-filter]
   [ui.data-grid.core]
   [ui.data-grid.theme :as ag-theme]
   [react :as react]
   [reagent.core :as reagent]))

(defn- col-field [idx]
  (str "col" idx))

(defn- corner-header
  [toggle-whole-table-selected]
  (fn [_props]
    (reagent/as-element
     [:div {:class    "w-full h-full cursor-pointer"
            :title    "Select all"
            :on-click (fn [] (toggle-whole-table-selected))}])))

(defn- ->row-number-col-def [dark-mode? toggle-whole-table-selected]
  (clj->js
   {"colId" "row-number" "headerName" "" "headerComponent" (corner-header toggle-whole-table-selected)
    "valueGetter" (fn [params] (inc (j/get-in params [:node :rowIndex])))
    "width" 46 "minWidth" 46 "maxWidth" 46 "pinned" "left"
    "lockPinned" true "lockPosition" true "suppressMovable" true
    "sortable" false "filter" false "resizable" false "editable" false
    "cellStyle" {"textAlign" "center"
                  "backgroundColor" (ag-theme/header-background dark-mode?)
                  "color" (ag-theme/text-color dark-mode?)}}))

(defn- ->column-defs [headers dark-mode? filterable? row-numbers? header-tooltips? toggle-whole-table-selected]
  (into-array
   (cond->> (map-indexed (fn [idx header]
                           (clj->js (cond-> {"field" (col-field idx)
                                            "headerName" header
                                            "filter" (if filterable?
                                                       condition-value-filter/condition-value-filter
                                                       false)}
                                      header-tooltips?
                                      (assoc "headerTooltip" header))))
                         headers)
     row-numbers? (cons (->row-number-col-def dark-mode? toggle-whole-table-selected)))))

(defn- ->row-data [rows]
  (clj->js
   (mapv (fn [row]
           (into {} (map-indexed (fn [idx value] [(col-field idx) value]) row)))
         rows)))

(defn displayed-rows [grid-api column-count fallback-rows]
  (if grid-api
    (let [rows (volatile! [])]
      (j/call grid-api :forEachNodeAfterFilterAndSort
              (fn [node]
                (let [row-data (j/get node :data)]
                  (vswap! rows conj
                          (mapv #(j/get row-data (col-field %))
                                (range column-count))))))
      @rows)
    fallback-rows))

(defn table
  [{:keys [headers rows dark-mode? height column-width sortable? filterable?
           resizable? row-numbers? popup-parent suppress-no-rows-overlay?
           header-tooltips? ensure-dom-order? on-grid-ready]}]
  (let [[whole-table-selected? set-whole-table-selected?] (react/useState false)
        grid-api-ref (react/useRef nil)
        toggle-whole-table-selected
        (react/useCallback #(set-whole-table-selected? not) #js [])
        clear-whole-table-selected
        (react/useCallback #(set-whole-table-selected? false) #js [])
        copy-whole-table!
        (react/useCallback
         (fn []
           (copy (j/call papaparse :unparse
                         (clj->js {:data (displayed-rows (j/get grid-api-ref :current)
                                                        (count headers)
                                                        rows)})
                         #js {:newline "\r\n" :delimiter "\t"})
                 #js {:format "text/plain"}))
         #js [headers rows])
        row-data        (react/useMemo #(->row-data rows) #js [rows])
        column-defs     (react/useMemo
                         #(->column-defs headers dark-mode? filterable? row-numbers? header-tooltips? toggle-whole-table-selected)
                         #js [headers dark-mode? filterable? row-numbers? header-tooltips? toggle-whole-table-selected])
        default-col-def (react/useMemo
                         #(clj->js {"sortable" sortable?
                                    "resizable" resizable?
                                    "width" column-width
                                    "cellDataType" false})
                         #js [column-width sortable? resizable?])
        {resize-on-grid-ready :on-grid-ready
         on-column-resized   :on-column-resized} (column-resize/use-auto-size-toggle whole-table-selected?)
        _ (react/useEffect
           (fn []
             (if whole-table-selected?
               (let [handler (fn [e]
                                (when (and (or (j/get e :metaKey) (j/get e :ctrlKey))
                                           (#{"c" "C"} (j/get e :key)))
                                  (.preventDefault e)
                                  (.stopPropagation e)
                                  (copy-whole-table!)))]
                 (.addEventListener js/document "keydown" handler true)
                 #(.removeEventListener js/document "keydown" handler true))
               js/undefined))
           #js [whole-table-selected? copy-whole-table!])
        grid-props      (cond-> {:theme             (ag-theme/for-mode dark-mode?)
                                 :row-data           row-data
                                 :column-defs        column-defs
                                 :default-col-def    default-col-def
                                 :row-height         23
                                 :enable-cell-text-selection true
                                 :ensure-dom-order   (boolean ensure-dom-order?)
                                 :on-cell-clicked    clear-whole-table-selected
                                 :on-grid-ready      (fn [event]
                                                       (j/assoc! grid-api-ref :current
                                                                 (j/get event :api))
                                                       (resize-on-grid-ready event)
                                                       (when on-grid-ready
                                                         (on-grid-ready event)))
                                 :on-column-resized  on-column-resized}
                          popup-parent
                          (assoc :popup-parent popup-parent)

                          (some? suppress-no-rows-overlay?)
                          (assoc :suppress-no-rows-overlay suppress-no-rows-overlay?))]
    [:div {:style {:height height :width "100%"}
           :class (when whole-table-selected? "kleene-grid-whole-table-selected")}
     [:> AgGridReact grid-props]]))
